const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/diagnostics`;
const SESSION_KEY = "ysq_diagnostics_session_v2";
const REPORTS_KEY = "ysq_diagnostics_reports_v2";
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
let installed = false;
let currentSession = null;
let breadcrumbs = [];
let networkErrors = [];
let lastFatalAt = 0;

function id() {
  return globalThis.crypto?.randomUUID?.() || `diag-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
function read(key, fallback = null) {
  try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; } catch { return fallback; }
}
function write(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}
function reports() {
  const now = Date.now();
  const list = (read(REPORTS_KEY, []) || []).filter(r => {
    const t = Date.parse(r?.occurred_at || "");
    return Number.isFinite(t) && now - t <= MAX_AGE;
  }).slice(0, 4);
  write(REPORTS_KEY, list);
  return list;
}
function save(list) {
  write(REPORTS_KEY, list.slice(0, 4));
  window.dispatchEvent(new CustomEvent("ysq:diagnostic-change"));
}
function cleanText(value, limit = 40000) {
  return String(value ?? "")
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [REDACTED]")
    .replace(/\beyJ[A-Za-z0-9_-]{12,}\.[A-Za-z0-9_-]{12,}\.[A-Za-z0-9_-]{8,}\b/g, "[REDACTED_JWT]")
    .slice(0, limit);
}
export function safeDiagnosticPath(value) {
  try {
    const url = new URL(value || window.location.href, window.location.origin);
    const allowed = new Set([window.location.origin]);
    if (BACKEND_URL) allowed.add(new URL(BACKEND_URL, window.location.origin).origin);
    if (!allowed.has(url.origin)) return "[external]";
    return url.pathname
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id")
      .replace(/\b[0-9a-f]{24}\b/gi, ":id")
      .replace(/\b\d{7,}\b/g, ":id")
      .slice(0, 500);
  } catch { return "[unknown]"; }
}
function build(kind, error, extra = {}) {
  const e = error instanceof Error ? error : null;
  return {
    client_report_id: id(),
    source: "web",
    kind,
    severity: extra.severity || "error",
    occurred_at: new Date().toISOString(),
    session_id: currentSession?.id || null,
    route: safeDiagnosticPath(window.location.href),
    message: cleanText(e?.message || error || "Application ended unexpectedly", 6000),
    stack: cleanText(e?.stack || extra.stack || ""),
    component_stack: cleanText(extra.componentStack || ""),
    release: process.env.REACT_APP_RELEASE || process.env.REACT_APP_VERSION || "web",
    build_sha: process.env.REACT_APP_BUILD_SHA || process.env.REACT_APP_GIT_SHA || "unknown",
    environment: {
      user_agent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.userAgentData?.platform || navigator.platform || "unknown",
      screen: `${screen.width}x${screen.height}`,
      viewport: `${innerWidth}x${innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown",
      online: navigator.onLine
    },
    breadcrumbs: breadcrumbs.slice(-50),
    network_errors: networkErrors.slice(-25),
    metadata: {
      session_end_confidence: kind === "unexpected_session_end" ? "uncertain" : "application_error",
      ...extra.metadata
    }
  };
}
export function recordNavigation(path) {
  breadcrumbs.push({ at: new Date().toISOString(), action: "navigation", path: safeDiagnosticPath(path) });
  breadcrumbs = breadcrumbs.slice(-50);
}
export function recordNetworkError({ method, url, status, duration_ms, request_id }) {
  const path = safeDiagnosticPath(url);
  if (path.includes("/api/diagnostics/")) return;
  networkErrors.push({
    at: new Date().toISOString(),
    method: String(method || "GET").toUpperCase().slice(0, 12),
    path,
    status: Number.isFinite(Number(status)) ? Number(status) : null,
    duration_ms: Number.isFinite(Number(duration_ms)) ? Math.round(Number(duration_ms)) : null,
    request_id: request_id ? cleanText(request_id, 120) : null
  });
  networkErrors = networkErrors.slice(-25);
}
export function captureDiagnostic(kind, error, extra = {}) {
  const now = Date.now();
  if (kind !== "unexpected_session_end" && now - lastFatalAt < 1200) return getPendingReport();
  if (kind !== "unexpected_session_end") lastFatalAt = now;
  const report = build(kind, error, extra);
  save([report, ...reports().filter(r => r.client_report_id !== report.client_report_id)]);
  return report;
}
export function captureReactError(error, componentStack) {
  return captureDiagnostic("react_render_error", error, { componentStack });
}
export function getPendingReport() {
  return reports().find(r => !r.dismissed_at) || null;
}
export function dismissReport(clientReportId) {
  save(reports().map(r => r.client_report_id === clientReportId
    ? { ...r, dismissed_at: new Date().toISOString() } : r));
}
function updateReceipt(clientReportId, receipt) {
  save(reports().map(r => r.client_report_id === clientReportId
    ? { ...r, receipt, submitted_at: new Date().toISOString() } : r));
}
export async function submitReport(report) {
  if (report?.receipt?.report_id && report?.receipt?.download_token) return report.receipt;
  const payload = { ...report };
  delete payload.receipt;
  delete payload.dismissed_at;
  delete payload.submitted_at;
  const response = await fetch(`${API}/reports`, {
    method: "POST",
    credentials: "omit",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error(`Diagnostic service returned ${response.status}`);
  const receipt = await response.json();
  updateReceipt(report.client_report_id, receipt);
  return receipt;
}
export async function downloadReport(report) {
  const current = reports().find(r => r.client_report_id === report.client_report_id) || report;
  const receipt = current.receipt || await submitReport(current);
  const link = document.createElement("a");
  link.href = `${API}/reports/${encodeURIComponent(receipt.report_id)}/download?token=${encodeURIComponent(receipt.download_token)}`;
  link.download = `Yasharal-Crash-Report-${receipt.report_id}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  return receipt;
}
function closeSession() {
  if (!currentSession) return;
  currentSession = { ...currentSession, active: false, clean_exit: true, last_heartbeat: Date.now() };
  write(SESSION_KEY, currentSession);
}
export function installDiagnostics() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const previous = read(SESSION_KEY);
  const age = previous?.last_heartbeat ? Date.now() - previous.last_heartbeat : Infinity;
  if (previous?.active === true && previous?.clean_exit !== true && age >= 0 && age <= MAX_AGE && !getPendingReport()) {
    const report = build("unexpected_session_end", "The previous web session ended without a clean shutdown signal.", {
      severity: "warning",
      metadata: { previous_session_id: previous.id || null, previous_route: previous.route || null }
    });
    report.session_id = previous.id || null;
    report.route = previous.route || report.route;
    save([report, ...reports()]);
  }
  currentSession = {
    id: id(), active: true, clean_exit: false,
    started_at: new Date().toISOString(), last_heartbeat: Date.now(),
    route: safeDiagnosticPath(window.location.href)
  };
  write(SESSION_KEY, currentSession);
  setInterval(() => {
    if (!currentSession) return;
    currentSession = { ...currentSession, active: true, clean_exit: false, last_heartbeat: Date.now(), route: safeDiagnosticPath(location.href) };
    write(SESSION_KEY, currentSession);
  }, 5000);
  addEventListener("pagehide", closeSession, { capture: true });
  addEventListener("beforeunload", closeSession, { capture: true });
  addEventListener("error", event => {
    if (event?.error) captureDiagnostic("uncaught_javascript_error", event.error);
    else if (event?.message) captureDiagnostic("window_error", event.message);
  });
  addEventListener("unhandledrejection", event => {
    captureDiagnostic("unhandled_promise_rejection", event.reason || "Unhandled promise rejection");
  });
}
